/** @format */

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  collection,
  query,
  getDocs,
  orderBy,
  limit,
  startAfter,
  where,
  doc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { Course, CourseBook, CourseSection } from "../types";

export const useCourses = (options?: { autoFetch?: boolean; useCacheOnInit?: boolean }) => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [featuredCourses, setFeaturedCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [lastDoc, setLastDoc] = useState<any>(null);
  const initializedFromCacheRef = useRef(false);

  const COURSES_CACHE_KEY = 'courses_cache_v1';
  const COURSES_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
  const FEATURED_COURSES_CACHE_KEY = 'featured_courses_cache_v1';
  const FEATURED_COURSES_CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

  const autoFetch = options?.autoFetch ?? true;
  const useCacheOnInit = options?.useCacheOnInit ?? true;

  // Try to load from cache on mount for instant paint
  useEffect(() => {
    if (!useCacheOnInit) return;
    try {
      const cachedRaw = localStorage.getItem(COURSES_CACHE_KEY);
      if (cachedRaw) {
        const cached = JSON.parse(cachedRaw) as { data: Course[]; updatedAt: number };
        if (Array.isArray(cached.data)) {
          setCourses(cached.data);
          setHasMore(false);
          initializedFromCacheRef.current = true;
        }
      }
    } catch (e) {
      // Ignore cache errors and proceed
    }
  }, [useCacheOnInit]);

  // Helper function to process course data
  const processCourseData = (doc: any): Course => {
    const courseData = doc.data();
    return {
      id: doc.id,
      name: courseData.name || "",
      image_url: courseData.image_url || "",
      author: {
        id: courseData.author?.id || "",
        name: courseData.author?.name || "",
        image_url: courseData.author?.image_url || "",
        status: courseData.author?.status || "",
        zoomId: courseData.author?.zoomId || "",
      },
      author_details: {
        status: courseData.author_details?.status || "",
        zoomId: courseData.author_details?.zoomId || "",
      },
      author_id: courseData.author_id || "",
      author_status: courseData.author_status || "",
      description: courseData.description || "",
      summary: courseData.summary || "",
      duration: courseData.duration || "",
      language: courseData.language || "",
      pricee: courseData.pricee || "",
      price_status: courseData.price_status || "",
      rating: courseData.rating || 0,
      students: courseData.students || 0,
      learnings: courseData.learnings || "",
      requirements: courseData.requirements || "",
      tag_ids: courseData.tag_ids || [],
      enrolled_students: courseData.enrolled_students || [],
      lessons_count: courseData.lessons_count || 0,
      books: courseData.books || [],
      sections: courseData.sections || [],
      status: courseData.status || "",
      userId: courseData.userId || "",
      cat_id: courseData.cat_id || "",
      video_url: courseData.video_url || "",
      zoom_id: courseData.zoom_id || "",
      meta: courseData.meta || {
        description: "",
        duration: "",
        language: "",
        learnings: [],
        requirements: [],
      },
      created_at: courseData.created_at || null,
      updated_at: courseData.updated_at || null,
      program: courseData.program || [],
      featured: courseData.featured ?? false,
    };
  };

  // Fetch courses from Firestore
  const fetchCourses = useCallback(async (filters?: {
    price_status?: string;
    language?: string;
    search?: string;
    userProgram?: string;
    featured?: boolean;
    limit?: number; // Add limit parameter
  }) => {
    try {
      // Avoid showing a loading spinner if we have cached data
      setLoading(!initializedFromCacheRef.current);
      setError(null);

      let q = query(collection(db, "courses"), orderBy("name", "asc"));
      
      // Apply limit to prevent massive reads
      const queryLimit = filters?.limit || 50; // Default to 50 courses
      q = query(q, limit(queryLimit));

      // Apply filters
      if (filters?.price_status && filters.price_status !== "all") {
        q = query(q, where("price_status", "==", filters.price_status));
      }

      if (filters?.language && filters.language !== "all") {
        q = query(q, where("language", "==", filters.language));
      }

      // Filter by featured courses if specified
      if (filters?.featured === true) {
        q = query(q, where("featured", "==", true));
      }

      // Filter by user program if specified
      if (filters?.userProgram && filters.userProgram !== "all") {
        // For now, let's fetch all courses and filter in memory
        // This handles both string and array program fields
      }

      const querySnapshot = await getDocs(q);

      const coursesData: Course[] = [];

      querySnapshot.forEach((doc) => {
        const courseData = processCourseData(doc);
        // Don't include draft courses
        if (courseData.status !== "draft") {
          coursesData.push(courseData);
        }
      });

      // Apply program filtering in memory if needed
      let finalCourses = coursesData;
      if (filters?.userProgram && filters.userProgram !== "all") {
        const userProgram = filters.userProgram; // Type guard
        finalCourses = coursesData.filter(course => {
          if (!course.program) return false;
          let matches = false;
          if (Array.isArray(course.program)) {
            matches = course.program.includes(userProgram);
          } else {
            matches = course.program === userProgram;
          }
          return matches;
        });
      }

      setCourses(finalCourses);
      setHasMore(querySnapshot.size === queryLimit); // Set hasMore based on limit
      // Persist to cache with timestamp
      try {
        localStorage.setItem(COURSES_CACHE_KEY, JSON.stringify({ data: finalCourses, updatedAt: Date.now() }));
      } catch (e) {
        // Ignore cache write errors
      }
      setLastDoc(querySnapshot.docs[querySnapshot.docs.length - 1]); // Store last doc for pagination
      
    } catch (err) {
      setError("Failed to load courses");
    } finally {
      setLoading(false);
    }
  }, []);

  // Load more courses (pagination)
  const loadMoreCourses = useCallback(async () => {
    if (!hasMore || !lastDoc) return;
    
    try {
      setLoading(true);
      const q = query(
        collection(db, "courses"),
        orderBy("name", "asc"),
        startAfter(lastDoc),
        limit(50)
      );
      
      const querySnapshot = await getDocs(q);
      const newCourses = querySnapshot.docs.map(doc => processCourseData(doc));
      
      setCourses(prev => [...prev, ...newCourses]);
      setHasMore(querySnapshot.size === 50);
      setLastDoc(querySnapshot.docs[querySnapshot.docs.length - 1]);
    } catch (err) {
      setError("Failed to load more courses");
    } finally {
      setLoading(false);
    }
  }, [hasMore, lastDoc]);

  // Search courses with limit
  const searchCourses = useCallback(async (searchTerm: string) => {
    try {
      setLoading(true);
      setError(null);

      const q = query(query(collection(db, "courses"), orderBy("name", "asc")), limit(100)); // Limit search results
      const querySnapshot = await getDocs(q);
      const allCourses: Course[] = [];

      querySnapshot.forEach((doc) => {
        allCourses.push(processCourseData(doc));
      });

      // Filter courses by search term
      const filteredCourses = allCourses.filter((course) => {
        const searchTermLower = searchTerm.toLowerCase();

        // Helper function to safely search through books
        const searchBooks = (books: (string | CourseBook)[]) => {
          if (!Array.isArray(books)) return false;
          return books.some((book) => {
            if (typeof book === "string") return book.toLowerCase().includes(searchTermLower);
            return book.title?.toLowerCase().includes(searchTermLower) || 
                   book.description?.toLowerCase().includes(searchTermLower);
          });
        };

        return (
          course.name.toLowerCase().includes(searchTermLower) ||
          course.description.toLowerCase().includes(searchTermLower) ||
          course.summary.toLowerCase().includes(searchTermLower) ||
          course.author.name.toLowerCase().includes(searchTermLower) ||
          searchBooks(course.books)
        );
      });

      setCourses(filteredCourses);
      setLastDoc(null);
      setHasMore(false);
    } catch (err) {
      setError("Failed to search courses");
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch featured courses specifically
  const fetchFeaturedCourses = useCallback(async (maxResults: number = 6) => {
    try {
      setError(null);

      // Try cache first (SWR): seed UI from cache; if stale, refresh in background without spinner
      let hasSeededFromCache = false;
      let isCacheFresh = false;
      try {
        const cachedRaw = localStorage.getItem(FEATURED_COURSES_CACHE_KEY);
        if (cachedRaw) {
          const cached = JSON.parse(cachedRaw) as { data: Course[]; updatedAt: number };
          if (cached?.data && Array.isArray(cached.data)) {
            setFeaturedCourses(cached.data);
            hasSeededFromCache = true;
            isCacheFresh = !!(cached.updatedAt && (Date.now() - cached.updatedAt) < FEATURED_COURSES_CACHE_TTL_MS);
          }
        }
      } catch (_) {
        // ignore cache read errors
      }

      if (isCacheFresh) {
        // Data is fresh enough; skip network read
        return;
      }

      // Only show spinner if we have nothing to show
      if (!hasSeededFromCache) {
        setLoading(true);
      }
      
      const q = query(
        collection(db, "courses"), 
        where("featured", "==", true),
        // Prioritize highest-rated featured courses; Firestore can sort on a single field here
        orderBy("rating", "desc"),
        limit(maxResults)
      );
      
      const querySnapshot = await getDocs(q);
      const featuredCoursesData: Course[] = [];

      querySnapshot.forEach((doc) => {
        const courseData = processCourseData(doc);
        // Don't include draft courses in featured courses
        if (courseData.status !== "draft") {
          featuredCoursesData.push(courseData);
        }
      });

      // Sort by rating first, then by students
      const sortedCourses = featuredCoursesData.sort((a, b) => {
        if (b.rating !== a.rating) {
          return b.rating - a.rating;
        }
        return (b.students || 0) - (a.students || 0);
      });

      // Enforce maxResults just in case
      const finalFeatured = sortedCourses.slice(0, maxResults);
      setFeaturedCourses(finalFeatured);
      
      // Write to cache
      try {
        localStorage.setItem(
          FEATURED_COURSES_CACHE_KEY,
          JSON.stringify({ data: finalFeatured, updatedAt: Date.now() })
        );
      } catch (_) {
        // ignore cache write errors
      }
      setLastDoc(null);
      setHasMore(false);
      
    } catch (err) {
      console.error('Error fetching featured courses:', err);
      setError("Failed to load featured courses");
    } finally {
      setLoading(false);
    }
  }, []);

  // Get unique languages from courses
  const getLanguages = useCallback(async (): Promise<string[]> => {
    try {
      // Bounded scan to derive languages
      const q = query(collection(db, "courses"), limit(500));
      const querySnapshot = await getDocs(q);
      const languages = new Set<string>();

      querySnapshot.forEach((doc) => {
        const courseData = doc.data();
        if (courseData.language) {
          languages.add(courseData.language);
        }
      });

      return Array.from(languages).sort();
    } catch (err) {
      return [];
    }
  }, []);

  // Fetch courses created by a specific author
  const fetchAuthorCourses = useCallback(async (authorId: string) => {
    try {
      setLoading(true);
      setError(null);

      // Query courses where either userId OR author_id matches the author
      const [userIdQuery, authorIdQuery] = await Promise.all([
        getDocs(
          query(
            collection(db, "courses"),
            where("userId", "==", authorId),
            orderBy("name", "asc"),
            limit(100)
          )
        ),
        getDocs(
          query(
            collection(db, "courses"),
            where("author_id", "==", authorId),
            orderBy("name", "asc"),
            limit(100)
          )
        )
      ]);

      const authorCourses: Course[] = [];
      const seenIds = new Set<string>();

      // Process userId results
      userIdQuery.forEach((doc) => {
        if (!seenIds.has(doc.id)) {
          const courseData = processCourseData(doc);
          // Don't include draft courses
          if (courseData.status !== "draft") {
            authorCourses.push(courseData);
            seenIds.add(doc.id);
          }
        }
      });

      // Process author_id results
      authorIdQuery.forEach((doc) => {
        if (!seenIds.has(doc.id)) {
          const courseData = processCourseData(doc);
          // Don't include draft courses
          if (courseData.status !== "draft") {
            authorCourses.push(courseData);
            seenIds.add(doc.id);
          }
        }
      });

      setCourses(authorCourses);
      setHasMore(false);
      setLastDoc(null);
      
      // Clear any previous errors since we successfully loaded courses
      setError(null);

      // Cache result
      try {
        localStorage.setItem(`author_courses_${authorId}`, JSON.stringify({ 
          data: authorCourses, 
          updatedAt: Date.now() 
        }));
      } catch (e) {
        // Ignore cache write errors
      }

    } catch (err) {
      setError("Failed to load author courses");
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch all courses (for admin users)
  const fetchAllCourses = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const q = query(
        collection(db, "courses"),
        orderBy("name", "asc"), // Use name for more reliable ordering
        limit(200) // Higher limit for admin view
      );

      const querySnapshot = await getDocs(q);
      const allCourses: Course[] = [];

      querySnapshot.forEach((doc) => {
        const courseData = processCourseData(doc);
        // Don't include draft courses
        if (courseData.status !== "draft") {
          allCourses.push(courseData);
        }
      });

      console.log('[fetchAllCourses] Fetched courses:', allCourses.length, 'courses');
      setCourses(allCourses);
      setHasMore(false);
      setLastDoc(null);

      // Cache result
      try {
        localStorage.setItem('admin_all_courses', JSON.stringify({ 
          data: allCourses, 
          updatedAt: Date.now() 
        }));
      } catch (e) {
        // Ignore cache write errors
      }

    } catch (err) {
      setError("Failed to load all courses");
    } finally {
      setLoading(false);
    }
  }, []);

  // Update course
  const updateCourse = useCallback(async (courseId: string, updates: Partial<Course>) => {
    try {
      setLoading(true);
      setError(null);

      const courseRef = doc(db, "courses", courseId);
      
      // Prepare update data (exclude fields that shouldn't be updated)
      const { id, created_at, ...updateData } = updates;
      
      // Add updated_at timestamp
      const finalUpdates = {
        ...updateData,
        updated_at: new Date().toISOString()
      };

      await updateDoc(courseRef, finalUpdates);

      // Update local state
      setCourses(prev => prev.map(course => 
        course.id === courseId 
          ? { ...course, ...finalUpdates }
          : course
      ));

      console.log('[updateCourse] Course updated successfully:', courseId);
    } catch (err) {
      console.error('[updateCourse] Error updating course:', err);
      setError("Failed to update course");
      throw new Error("Failed to update course");
    } finally {
      setLoading(false);
    }
  }, []);

  // Refresh courses
  const refreshCourses = useCallback(() => {
    setCourses([]);
    setLastDoc(null);
    setHasMore(true);
    fetchCourses();
  }, [fetchCourses]);

  // Auto-fetch on mount with limit; if cache is fresh, do silent background refresh
  useEffect(() => {
    if (!autoFetch) return;

    let shouldRefreshInBackground = false;
    try {
      const cachedRaw = localStorage.getItem(COURSES_CACHE_KEY);
      if (cachedRaw) {
        const cached = JSON.parse(cachedRaw) as { data: Course[]; updatedAt: number };
        if (cached?.updatedAt && Date.now() - cached.updatedAt < COURSES_CACHE_TTL_MS) {
          // Cache is fresh enough; background refresh without spinner
          shouldRefreshInBackground = true;
        }
      }
    } catch (e) {
      // If any issue with cache, do normal fetch
    }

    if (shouldRefreshInBackground) {
      // Temporarily mark as initialized from cache to prevent spinner, then reset
      const prev = initializedFromCacheRef.current;
      initializedFromCacheRef.current = true;
      fetchCourses({ limit: 50 }).finally(() => { // Limit initial fetch to 50 courses
        initializedFromCacheRef.current = prev;
      });
    } else {
      fetchCourses({ limit: 50 }); // Limit initial fetch to 50 courses
    }
  }, [autoFetch]); // Remove fetchCourses and COURSES_CACHE_TTL_MS from dependencies

  // Memoize the return object to prevent unnecessary re-renders
  const returnValue = useMemo(() => ({
    courses,
    featuredCourses,
    loading,
    error,
    hasMore,
    fetchCourses,
    fetchFeaturedCourses,
    searchCourses,
    getLanguages,
    refreshCourses,
    loadMoreCourses,
    fetchAuthorCourses,
    fetchAllCourses,
    updateCourse,
  }), [
    courses,
    featuredCourses,
    loading,
    error,
    hasMore,
    fetchCourses,
    fetchFeaturedCourses,
    searchCourses,
    getLanguages,
    refreshCourses,
    loadMoreCourses,
    fetchAuthorCourses,
    fetchAllCourses,
    updateCourse,
  ]);

  return returnValue;
};
